<template>
  <div class="localization-form">
    <!-- Header with language selector and action buttons -->
    <div class="localization-form__header">
      <div class="localization-form__dropdown-wrapper">
        <DropdownInput
          v-model="selectedLanguage"
          :options="languageOptions"
          placeholder="Select a language..."
        />
      </div>
      <!-- Step 1: Download/Copy buttons (when no template action yet) -->
      <div v-if="!disabled && !templateAction" class="localization-form__actions">
        <button
          type="button"
          class="localization-form__icon-btn"
          title="Download YAML template"
          @click="handleDownload"
        >
          <vue-feather type="download" size="18" />
        </button>
        <button
          type="button"
          class="localization-form__icon-btn"
          title="Copy YAML template to clipboard"
          @click="handleCopy"
        >
          <vue-feather type="copy" size="18" />
        </button>
      </div>
      <!-- Step 2: Upload button (after download) -->
      <div v-if="!disabled && templateAction === 'download'" class="localization-form__actions">
        <button
          type="button"
          class="localization-form__icon-btn"
          title="Upload completed YAML file"
          @click="triggerFileInput"
        >
          <vue-feather type="upload" size="18" />
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".yml,.yaml"
          class="localization-form__file-input"
          @change="handleFileSelect"
        />
      </div>
      <!-- Step 2: Paste button (after copy) -->
      <div v-if="!disabled && templateAction === 'copy'" class="localization-form__actions">
        <button
          type="button"
          class="localization-form__icon-btn"
          title="Paste YAML from clipboard"
          @click="handlePaste"
        >
          <vue-feather type="clipboard" size="18" />
        </button>
      </div>
    </div>

    <!-- Error message for upload/paste -->
    <div v-if="uploadError" class="localization-form__error">
      {{ uploadError }}
    </div>

    <!-- Success message for upload/paste -->
    <div v-if="uploadSuccess" class="localization-form__success">
      {{ uploadSuccess }}
    </div>

    <!-- Fields Table -->
    <div class="localization-form__table" v-if="selectedLanguage">
      <div
        v-for="field in formFields"
        :key="field.id"
        class="localization-form__row"
      >
        <div class="localization-form__label">
          {{ field.label }}
        </div>
        <div class="localization-form__value">
          <input
            type="text"
            class="localization-form__input"
            :value="getFieldValue(field.id)"
            :placeholder="getPlaceholder(field)"
            :disabled="disabled"
            @input="setFieldValue(field.id, $event.target.value)"
            @keydown.stop
            @paste.stop
          />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="localization-form__empty">
      <vue-feather type="globe" size="24" class="localization-form__empty-icon" />
      <span>Select a language above to edit translations</span>
    </div>

    <!-- Status footer -->
    <div class="localization-form__footer" v-if="selectedLanguage">
      <span class="localization-form__status">
        <vue-feather
          :type="statusIcon"
          size="14"
          class="localization-form__status-icon"
          :class="statusIconClass"
        />
        {{ statusText }}
      </span>
    </div>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import DropdownInput from './DropdownInput.vue'
import yaml from 'js-yaml'

export default {
  name: 'LocalizationForm',
  components: {
    'vue-feather': VueFeather,
    DropdownInput
  },
  props: {
    // Field definitions: [{ id, label, hint? }]
    formFields: {
      type: Array,
      required: true,
      validator: (fields) => fields.every(f => 'id' in f && 'label' in f)
    },
    // Available languages from account settings
    languages: {
      type: Array,
      required: true
    },
    // Current translations: { English: { field: value }, ... }
    modelValue: {
      type: Object,
      default: () => ({})
    },
    // View-only mode when true
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedLanguage: '',
      templateAction: null, // null, 'download', or 'copy'
      uploadError: '',
      uploadSuccess: ''
    }
  },
  computed: {
    // Build dropdown options with field count subtext
    languageOptions() {
      return this.languages.map(lang => {
        const count = this.countFilledFieldsForLanguage(lang)
        const total = this.formFields.length
        let sublabel
        if (count === total) {
          sublabel = `${count}/${total} fields (complete)`
        } else if (count > 0) {
          sublabel = `${count}/${total} fields`
        } else {
          sublabel = 'Not set up'
        }
        return {
          value: lang,
          label: lang,
          sublabel
        }
      })
    },
    // Get the current language's translations
    currentTranslations() {
      return this.modelValue[this.selectedLanguage] || {}
    },
    // Count of translated fields for current language
    translatedCount() {
      return this.countFilledFieldsForLanguage(this.selectedLanguage)
    },
    // Total number of fields
    totalFields() {
      return this.formFields.length
    },
    // Status text for footer
    statusText() {
      if (this.translatedCount === this.totalFields) {
        return `All ${this.totalFields} fields translated`
      }
      return `${this.translatedCount} of ${this.totalFields} fields translated`
    },
    // Status icon based on completion
    statusIcon() {
      if (this.translatedCount === this.totalFields) {
        return 'check-circle'
      }
      if (this.translatedCount > 0) {
        return 'clock'
      }
      return 'alert-circle'
    },
    // Status icon class for coloring
    statusIconClass() {
      if (this.translatedCount === this.totalFields) {
        return 'localization-form__status-icon--success'
      }
      if (this.translatedCount > 0) {
        return 'localization-form__status-icon--pending'
      }
      return 'localization-form__status-icon--warning'
    }
  },
  watch: {
    // Set default selection when languages change
    languages: {
      handler(newLanguages) {
        if (newLanguages.length > 0 && !newLanguages.includes(this.selectedLanguage)) {
          this.selectedLanguage = newLanguages[0]
        }
      },
      immediate: true
    },
    // Reset template action when language changes
    selectedLanguage() {
      this.templateAction = null
      this.clearMessages()
    }
  },
  mounted() {
    // Set initial selection
    if (this.languages.length > 0 && !this.selectedLanguage) {
      this.selectedLanguage = this.languages[0]
    }
  },
  methods: {
    // Count filled fields for a specific language
    countFilledFieldsForLanguage(lang) {
      const translations = this.modelValue[lang] || {}
      let count = 0
      for (const field of this.formFields) {
        const value = translations[field.id]
        if (value && value.trim().length > 0) {
          count++
        }
      }
      return count
    },
    // Get placeholder text for a field (use hint without quotes)
    getPlaceholder(field) {
      if (field.hint) {
        // Remove quotes from hint for cleaner placeholder
        return field.hint.replace(/^["']|["']$/g, '')
      }
      return 'Enter translation...'
    },
    // Get field value for current language
    getFieldValue(fieldId) {
      return this.currentTranslations[fieldId] || ''
    },
    // Set field value and emit update
    setFieldValue(fieldId, value) {
      // Clone the current modelValue
      const newValue = { ...this.modelValue }

      // Ensure the language object exists
      if (!newValue[this.selectedLanguage]) {
        newValue[this.selectedLanguage] = {}
      } else {
        newValue[this.selectedLanguage] = { ...newValue[this.selectedLanguage] }
      }

      // Set the field value
      newValue[this.selectedLanguage][fieldId] = value

      this.$emit('update:modelValue', newValue)
    },
    // Generate YAML template string
    generateYamlString() {
      const yamlContent = {
        translations: this.formFields.map(field => ({
          id: field.id,
          original: field.hint || field.label,
          translation: this.getFieldValue(field.id) || ''
        }))
      }

      return yaml.dump(yamlContent, {
        lineWidth: -1,
        quotingType: '"',
        forceQuotes: true
      })
    },
    // Download YAML template
    handleDownload() {
      if (!this.selectedLanguage) return
      this.clearMessages()

      const yamlString = this.generateYamlString()
      const blob = new Blob([yamlString], { type: 'text/yaml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `form_filler_${this.selectedLanguage.replace(/[^a-zA-Z0-9]/g, '_')}.yml`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      this.templateAction = 'download'
    },
    // Copy YAML template to clipboard
    async handleCopy() {
      if (!this.selectedLanguage) return
      this.clearMessages()

      const yamlString = this.generateYamlString()
      try {
        await navigator.clipboard.writeText(yamlString)
        this.uploadSuccess = 'Template copied to clipboard'
        this.templateAction = 'copy'
        setTimeout(() => this.clearMessages(), 3000)
      } catch (err) {
        console.error('Failed to copy to clipboard:', err)
        this.uploadError = 'Failed to copy to clipboard'
      }
    },
    // Trigger file input click
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    // Handle file selection
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) this.processFile(file)
      // Reset input so same file can be selected again
      event.target.value = ''
    },
    // Process uploaded file
    async processFile(file) {
      this.clearMessages()

      if (!file.name.endsWith('.yml') && !file.name.endsWith('.yaml')) {
        this.uploadError = 'Please upload a .yml or .yaml file'
        return
      }

      const content = await file.text()
      this.processYamlContent(content)
    },
    // Paste from clipboard
    async handlePaste() {
      if (!this.selectedLanguage) return
      this.clearMessages()

      try {
        const text = await navigator.clipboard.readText()
        if (!text) {
          this.uploadError = 'Clipboard is empty'
          return
        }
        this.processYamlContent(text)
      } catch (err) {
        console.error('Failed to read clipboard:', err)
        this.uploadError = 'Could not read clipboard. Please check browser permissions.'
      }
    },
    // Process YAML content and update fields
    processYamlContent(content) {
      try {
        const parsed = yaml.load(content)

        if (!parsed || !parsed.translations || !Array.isArray(parsed.translations)) {
          this.uploadError = 'Invalid file structure. Expected "translations" array.'
          return
        }

        // Build new translations object
        const newValue = { ...this.modelValue }
        if (!newValue[this.selectedLanguage]) {
          newValue[this.selectedLanguage] = {}
        } else {
          newValue[this.selectedLanguage] = { ...newValue[this.selectedLanguage] }
        }

        let count = 0
        for (const item of parsed.translations) {
          if (item.id && item.translation && item.translation.trim()) {
            // Only update if the field ID exists in our form fields
            const fieldExists = this.formFields.some(f => f.id === item.id)
            if (fieldExists) {
              newValue[this.selectedLanguage][item.id] = item.translation.trim()
              count++
            }
          }
        }

        if (count === 0) {
          this.uploadError = 'No valid translations found in file'
          return
        }

        this.$emit('update:modelValue', newValue)
        this.uploadSuccess = `Imported ${count} translation${count !== 1 ? 's' : ''}`
        setTimeout(() => this.clearMessages(), 3000)
      } catch (err) {
        console.error('Failed to parse YAML:', err)
        this.uploadError = 'Failed to parse YAML file'
      }
    },
    // Clear error/success messages
    clearMessages() {
      this.uploadError = ''
      this.uploadSuccess = ''
    },
    // Programmatically select a language
    selectLanguage(lang) {
      if (this.languages.includes(lang)) {
        this.selectedLanguage = lang
      }
    },
    // Get the currently selected language
    getSelectedLanguage() {
      return this.selectedLanguage
    }
  },
  emits: ['update:modelValue']
}
</script>

<style scoped>
.localization-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.localization-form__header {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.localization-form__dropdown-wrapper {
  flex: 1;
}

.localization-form__actions {
  display: flex;
  gap: 8px;
}

.localization-form__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  padding: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.localization-form__icon-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.localization-form__icon-btn:active {
  transform: scale(0.96);
}

.localization-form__file-input {
  display: none;
}

.localization-form__error {
  font-size: 0.8125rem;
  color: #dc2626;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
}

.localization-form__success {
  font-size: 0.8125rem;
  color: #16a34a;
  padding: 8px 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
}

/* Table Layout */
.localization-form__table {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.localization-form__row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 12px;
  align-items: center;
}

.localization-form__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.3;
}

.localization-form__value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.localization-form__input {
  width: 100%;
  min-height: 45px;
  padding: 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.localization-form__input:hover:not(:disabled) {
  border-color: #9ca3af;
}

.localization-form__input:focus {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.localization-form__input:disabled {
  background: #fafafa;
  color: #6b7280;
  cursor: not-allowed;
}

.localization-form__input::placeholder {
  color: #9ca3af;
}

/* Empty State */
.localization-form__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
  text-align: center;
  gap: 12px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px dashed #e5e7eb;
}

.localization-form__empty-icon {
  color: #d1d5db;
}

/* Footer */
.localization-form__footer {
  display: flex;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.localization-form__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
}

.localization-form__status-icon {
  flex-shrink: 0;
}

.localization-form__status-icon--success {
  color: #10b981;
}

.localization-form__status-icon--warning {
  color: #f59e0b;
}

.localization-form__status-icon--pending {
  color: #6b7280;
}

/* Responsive */
@media screen and (max-width: 600px) {
  .localization-form__header {
    flex-direction: column;
    align-items: stretch;
  }

  .localization-form__actions {
    justify-content: flex-end;
  }

  .localization-form__row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
